import React, {useEffect, useState} from 'react'
import AltNavbar from '../components/AltNavbar'
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Pricing from '../components/Pricing/Pricing';
import {useNavigate} from "react-router-dom";
import {hasBlankOrNullFields} from "../utilities/helpers";
import {validateEmail} from "../services/authService";
import {useAppContext} from "../context/appContext";


const steps = ['Personal Information', 'Account Information', 'Plan Selection'];

const Register = () => {
    const {registerUser} = useAppContext()

    const [activeStep, setActiveStep] = useState(0);
    const [skipped, setSkipped] = useState(new Set<number>());
    const [registerInfo, setRegisterInfo] = useState({
        firstName: '',
        lastName: '',
        gender: 'male',
        email: '',
        phone: '',
        password: '',
        confirmedPassword: '',
        customerType: 0
    });

    const navigate = useNavigate();
    const handleRegister = async () => {
        // Validate email
        if (!validateEmail(registerInfo.email)) {
            alert("Please enter a valid email address.");
            return;
        }
        const emptyField = Object.entries(registerInfo).find(([key, value]) => value === '');

        // Simple validation to ensure all fields are filled out
        if (emptyField) {
            alert(`Please fill out the ${emptyField[0]} field.`);
            return;
        }

        // Validate password and confirmPassword match
        if (registerInfo.password !== registerInfo.confirmedPassword) {
            alert("Passwords do not match.");
            return;
        }

        try {
            const response = await registerUser({
                    first_name: registerInfo.firstName,
                    last_name: registerInfo.lastName,
                    gender: registerInfo.gender as "male" | "female" | "nonBinary",
                    email: registerInfo.email,
                    phone: registerInfo.phone,
                    password: registerInfo.password,
                    customer_type: registerInfo.customerType as 0 | 1 | 2
                }
            );

            if (response.success) {
                navigate("/profile");
            } else {
                // Display the error message returned from the registerUser function
                const errorMessage = response.error;
                alert(errorMessage || "An error occurred. Please try again.");
            }
        } catch (error) {
            console.error("Error during registration:", error);
            alert("Unable to connect. Please check your internet connection and try again.");
        }
    };


    const isStepOptional = (step: number) => {
        return step === 1;
    };

    const isStepSkipped = (step: number) => {
        return skipped.has(step);
    };

    const handleNext = () => {
        let newSkipped = skipped;
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped(newSkipped);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };


    const handleSkip = () => {
        if (!isStepOptional(activeStep)) {
            // You probably want to guard against something like this,
            // it should never occur unless someone's actively trying to break something.
            throw new Error("You can't skip a step that isn't optional.");
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped((prevSkipped) => {
            const newSkipped = new Set(prevSkipped.values());
            newSkipped.add(activeStep);
            return newSkipped;
        });
    };

    const handleReset = () => {
        setActiveStep(0);
    };


    function handleSignUpClick(index: number) {
        setRegisterInfo(prevState => ({...prevState, customerType: index}))
    }

    return (
        <>
            <AltNavbar/>

            <div className='card'>
                <Box sx={{width: '100%'}}>
                    <div>
                        <div className="card-header">
                            <Typography sx={{mt: 2, mb: 1}}><strong>Step {activeStep + 1} of 3</strong></Typography>

                            <h3>Create Account</h3>
                        </div>

                    </div>
                    <Stepper activeStep={activeStep}>
                        {steps.map((label, index) => {
                            const stepProps: { completed?: boolean } = {};
                            const labelProps: {
                                optional?: React.ReactNode;
                            } = {};

                            return (
                                <Step key={label} {...stepProps}>
                                    <StepLabel {...labelProps}>{label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                    {activeStep === steps.length ? (
                        <React.Fragment>
                            <Typography sx={{mt: 2, mb: 1}}>
                                All steps completed - you&apos;re finished
                            </Typography>
                            <Box sx={{display: 'flex', flexDirection: 'row', pt: 2}}>
                                <Box sx={{flex: '1 1 auto'}}/>
                                <Button onClick={handleReset}>Reset</Button>
                            </Box>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            {activeStep === 0 ?

                                <div className="card-content">
                                    <label htmlFor="fname" id="fname-label">First Name</label>
                                    <input type="text" id="fname" name="fname" value={registerInfo.firstName}
                                           onChange={(event) => setRegisterInfo(prevState => ({
                                               ...prevState,
                                               firstName: event.target.value
                                           }))} required/>

                                    <label htmlFor="lname" id="lname-label">Last Name</label>
                                    <input type="text" id="lname" name="lname" value={registerInfo.lastName}
                                           onChange={(event) => setRegisterInfo(prevState => ({
                                               ...prevState,
                                               lastName: event.target.value
                                           }))} required/>

                                    <FormControl>
                                        <FormLabel id="demo-radio-buttons-group-label">Gender</FormLabel>
                                        <RadioGroup
                                            aria-labelledby="demo-radio-buttons-group-label"
                                            defaultValue="male"
                                            name="radio-buttons-group"
                                            value={registerInfo.gender}
                                            onChange={(event => setRegisterInfo((prevState => ({
                                                ...prevState,
                                                gender: event.target.value
                                            }))))}
                                        >
                                            <FormControlLabel value="male" control={<Radio/>} label="Male"/>
                                            <FormControlLabel value="female" control={<Radio/>} label="Female"/>
                                            <FormControlLabel value="nonBinary" control={<Radio/>} label="Non-Binary"/>
                                        </RadioGroup>
                                    </FormControl>

                                </div>
                                :
                                activeStep === 1 ?
                                    <div className="card-content">
                                        <label htmlFor="email" id="email-label">Email</label>
                                        <input type="email" id="email" name="email" value={registerInfo.email}
                                               onChange={(event) => setRegisterInfo(prevState => ({
                                                   ...prevState,
                                                   email: event.target.value
                                               }))} required/>

                                        <label htmlFor="phone" id="phone-label">Phone Number</label>
                                        <input type="text" id="phone" name="phone" value={registerInfo.phone}
                                               onChange={(event) => setRegisterInfo(prevState => ({
                                                   ...prevState,
                                                   phone: event.target.value
                                               }))} required/>

                                        <label htmlFor="password" id="password-label">Password</label>
                                        <input type="password" id="password" name="password"
                                               value={registerInfo.password}
                                               onChange={(event) => setRegisterInfo(prevState => ({
                                                   ...prevState,
                                                   password: event.target.value
                                               }))} required/>

                                        <label htmlFor="confirmPassword" id="confirm-password-label">Confirm
                                            Password</label>
                                        <input type="password" id="confirm-password" name="confirmPassword"
                                               value={registerInfo.confirmedPassword}
                                               onChange={(event) => setRegisterInfo(prevState => ({
                                                   ...prevState,
                                                   confirmedPassword: event.target.value
                                               }))} required/>

                                    </div>
                                    :
                                    activeStep == 2 ?
                                        <div className='price-card-container'>
                                            <Pricing onSignUpClick={handleSignUpClick}/>
                                        </div>
                                        :
                                        <></>
                            }
                            <Button type="submit"
                                    value="Next"
                                    id="login"
                                    onClick={activeStep === steps.length - 1 ? handleRegister : handleNext}
                            >{activeStep === steps.length - 1 ? 'Create Account' : 'Next'}
                            </Button>
                            <Button type="submit" size="medium" id="back" disabled={activeStep === 0}
                                    onClick={handleBack}>Back</Button>

                            {/* <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Button
                  color="inherit"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />

                <Button onClick={handleNext}>
                  {activeStep === steps.length - 1 ? 'Create Account' : 'Next'}
                </Button>
              </Box> */}
                        </React.Fragment>
                    )}
                </Box>

            </div>

        </>
    )
}

export default Register